import { t } from "@lingui/macro";
import { Gauge, KeyRound, ListChecks, ScanLine } from "lucide-react";

import { Seo } from "@/client/components/seo";

import { CtaBand, FeatureCard, PageHero, Section, SectionHeading } from "../_components/page-shell";

type Feature = {
  icon: typeof ScanLine;
  title: string;
  description: string;
};

type Step = {
  title: string;
  description: string;
};

export const AtsCheckerPage = () => {
  const features: Feature[] = [
    {
      icon: ScanLine,
      title: t`Formatting scan`,
      description: t`We flag columns, tables, images, and fonts that trip up parsers so your resume stays machine readable.`,
    },
    {
      icon: KeyRound,
      title: t`Keyword match`,
      description: t`Compare your resume against the job description and see which skills and terms you are missing.`,
    },
    {
      icon: ListChecks,
      title: t`Missing sections`,
      description: t`Get alerted when key sections like contact details, experience, or skills are absent or incomplete.`,
    },
    {
      icon: Gauge,
      title: t`Readability check`,
      description: t`Measure clarity, length, and structure so both software and recruiters can read your resume with ease.`,
    },
  ];

  const steps: Step[] = [
    {
      title: t`Paste or pick your resume`,
      description: t`Upload a file or select a resume you already built in Izicvs.`,
    },
    {
      title: t`Add the job`,
      description: t`Paste the job description you are targeting so we can compare against it.`,
    },
    {
      title: t`Get a score with fixes`,
      description: t`Receive an ATS score plus clear, specific suggestions to improve it.`,
    },
  ];

  return (
    <>
      <Seo
        title={t`Free ATS checker for your resume`}
        description={t`Check whether your resume passes applicant tracking systems: keyword match, quantified impact, action verbs and format, so you reach a real recruiter.`}
        path="/ats-checker"
        breadcrumb={t`ATS Checker`}
      />

      <PageHero
        eyebrow={t`ATS Checker`}
        icon={ScanLine}
        title={t`Make sure your resume gets read`}
        subtitle={t`Applicant tracking systems filter resumes before a human ever sees them. Our checker helps you pass that first screen and reach a real recruiter.`}
      />

      <Section>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((feature, index) => (
            <FeatureCard
              key={feature.title}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
              index={index}
            />
          ))}
        </div>
      </Section>

      <Section>
        <SectionHeading title={t`How it works`} />
        <div className="grid gap-6 md:grid-cols-3">
          {steps.map((step, index) => (
            <div
              key={step.title}
              className="rounded-2xl border border-border bg-background p-6 transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-xl hover:shadow-primary/5"
            >
              <span className="flex size-8 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary">
                {index + 1}
              </span>
              <h3 className="mt-4 text-base font-semibold text-neutral-800 dark:text-neutral-100">
                {step.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-neutral-600 dark:text-neutral-400">
                {step.description}
              </p>
            </div>
          ))}
        </div>
      </Section>

      <CtaBand
        title={t`See how your resume scores`}
        description={t`Run your resume through the Izicvs ATS checker and fix what is holding you back.`}
        ctaLabel={t`Check my resume`}
      />
    </>
  );
};
